// @ts-nocheck
import { FormEvent, useEffect, useState } from "react";

import { RichText } from "@atlassiansox/bxp-content-framework";

import {
    AnalyticsAttributesProvider,
    useIdentityUrl,
    useAnalytics,
    useAnalyticsAttributes,
    useQuery
} from "../../components";
import { UiLoginSignupFormModel } from "../../models";
import { UiSocialLoginSignupButton } from "../UiSocialLoginSignupButton";

import {
    BlockLinkButton,
    Heading,
    InputEmail,
    Section,
    SocialButtons,
    SubmitButton
} from "./UiLoginSignupForm.styles";

export type UiLoginSignupFormProps = {
    model: UiLoginSignupFormModel;
    navigationFn?: (url: string) => void;
};

const UiLoginSignupForm = ({ model, navigationFn }: UiLoginSignupFormProps) => {
    const {
        browserWarningMessage,
        continueButtonText,
        disableLegacyAppearance,
        emailFieldLabel,
        emailFieldPlaceholder,
        formFooterContent,
        formSeparatorText,
        formType = "signup",
        heading,
        prompt,
        socialButtons,
        termsOfService,
        useEmailButton
    } = model.content;
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();
    const query = useQuery();
    const getParam = (param: string) => query.get(param) ?? ""; // always return a string for Typescript simplicity
    const returnUrl = getParam("returnUrl");
    const confirmReturnUrl = getParam("confirmReturnUrl");
    const [email, setEmail] = useState(getParam("email"));
    const [emailValid, setEmailValid] = useState(false);
    const [browserNotSupported, setBrowserNotSupported] = useState(false);

    const eventContainer = formType === "signup" ? "signUpForm" : "logInForm";

    const signupUrl = useIdentityUrl({
        email,
        confirmReturnUrl,
        prompt,
        returnUrl
    });

    useEffect(() => {
        // Copied from https://bitbucket.org/trello/meta-compliant/src/6d43743a897f89c7840ee72eb344cad3ffda9d12/packages/common/js/login.coffee?at=main#lines-450
        setEmailValid(
            !!email.length && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        );
    }, [email]);

    useEffect(() => {
        const cookiesDisabled = !navigator.cookieEnabled;
        // @ts-ignore -> Internet Explorer `documentMode` property
        const hasDocumentMode = !!document.documentMode;
        const hasIEUserAgent = /MSIE|Trident/.test(navigator.userAgent);

        if (cookiesDisabled || hasDocumentMode || hasIEUserAgent) {
            setBrowserNotSupported(true);
        }
    }, []);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        // See BXPT-1352 for idiosyncratic tracking rationale
        sendTrackEvent("submittedUiEmailSignup", {
            ...analyticsAttributes,
            event: "submitted",
            eventComponent: "uiEmailSignup",
            eventContainer,
            href: "",
            label: continueButtonText,
            labelEnglish: disableLegacyAppearance ? "Get started" : "Continue",
            uuid: model.id
        });

        if (emailValid) {
            if (navigationFn) {
                navigationFn(signupUrl);
            } else {
                location.assign(signupUrl);
            }
        }
    };

    // eslint-disable-next-line react/no-unstable-nested-components -- TODO: extract component
    const BrowserWarningBanner = () => (
        <>
            {/* Javascript disabled */}
            {browserWarningMessage && (
                <noscript>
                    <RichText
                        document={browserWarningMessage}
                        className="text text-incompatible"
                    />
                </noscript>
            )}
            {/* Javascript enabled, check browser */}
            {browserNotSupported && browserWarningMessage && (
                <RichText
                    document={browserWarningMessage}
                    className="text text-incompatible"
                />
            )}
        </>
    );

    return (
        <AnalyticsAttributesProvider value={{ eventContainer }}>
            <Section disableLegacyAppearance={disableLegacyAppearance}>
                <BrowserWarningBanner />
                {heading && (
                    <Heading as={disableLegacyAppearance ? "h2" : "h1"}>
                        {heading}
                    </Heading>
                )}
                {useEmailButton ? (
                    <>
                        {termsOfService && (
                            <RichText
                                document={termsOfService}
                                className="text text-tos"
                            />
                        )}
                        <BlockLinkButton
                            appearance="Primary"
                            url={signupUrl}
                            onClick={() =>
                                sendTrackEvent("clickedEmailContinueButton", {
                                    ...analyticsAttributes,
                                    event: "clicked",
                                    eventContainer,
                                    eventComponent: "link",
                                    label: continueButtonText,
                                    labelEnglish: "Sign up with email",
                                    uuid: model.id,
                                    href: signupUrl
                                })
                            }
                        >
                            {continueButtonText}
                        </BlockLinkButton>
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <InputEmail
                            aria-label={emailFieldLabel}
                            data-testid="email-input"
                            id="email" // Used in AAAA team Pollinator tests
                            name="email"
                            onChange={(e) => setEmail(e.currentTarget.value)}
                            placeholder={emailFieldPlaceholder}
                            type="email"
                            value={email}
                        />
                        {termsOfService && (
                            <RichText
                                document={termsOfService}
                                className="text text-tos"
                            />
                        )}
                        <SubmitButton
                            disableLegacyAppearance={disableLegacyAppearance}
                            disabled={!emailValid}
                            data-testid="signup-submit"
                            id="signup-submit" // Used in AAAA team Pollinator tests
                            type="submit"
                        >
                            {continueButtonText}
                        </SubmitButton>
                    </form>
                )}

                {!email.length && (
                    <>
                        {formSeparatorText && (
                            <p className="text">{formSeparatorText}</p>
                        )}
                        <SocialButtons
                            disableLegacyAppearance={disableLegacyAppearance}
                        >
                            {socialButtons
                                ?.filter(
                                    ({ type }) =>
                                        type === "uiSocialLoginSignupButton"
                                )
                                .map((button, index) => (
                                    // @ts-expect-error - Loadable component type incompatibility
                                    <UiSocialLoginSignupButton
                                        key={index}
                                        appearance={
                                            disableLegacyAppearance
                                                ? "outline"
                                                : "shadow"
                                        }
                                        model={button}
                                        buttonType={formType}
                                    />
                                ))}
                        </SocialButtons>
                    </>
                )}
                {formFooterContent && (
                    <RichText
                        document={formFooterContent}
                        className="text text-footer"
                    />
                )}
            </Section>
        </AnalyticsAttributesProvider>
    );
};

export default UiLoginSignupForm;
